import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Cookie } from 'lucide-react'
import Button from '../ui/Button'
import { consent, type ConsentStatus } from '../../lib/consent'
import { trackConsent } from '../../lib/analytics/track'
import { useChatOpen } from '../../lib/concierge/chatPresence'

export default function CookieConsentBanner() {
  const { t } = useTranslation()
  const { lang = 'en' } = useParams<{ lang: string }>()
  const reduce = useReducedMotion()
  // Consent is an external store (localStorage), so we read it through
  // useSyncExternalStore. The THIRD arg (getServerSnapshot) returns null — the
  // "undecided" state — which is exactly what the prerender baked (Node has no
  // localStorage, so the banner is open in every prerendered page). React uses
  // that server snapshot for the hydration render, so a returning visitor whose
  // stored choice is 'granted'/'denied' does NOT mismatch the baked markup
  // (which would be React #418); the real value is adopted right after, hiding
  // the banner. The subscribe arg also keeps the banner reactive to set().
  const status = useSyncExternalStore<ConsentStatus>(
    consent.subscribe,
    consent.get,
    () => null,
  )
  // The concierge chat and this banner are both bottom-fixed overlays. While the
  // chat panel is open we step the banner aside so it can't occlude the chat or
  // intercept taps on the consent checkbox / Start button on mobile. The undecided
  // consent state is preserved — the banner returns the moment the chat closes.
  const chatOpen = useChatOpen()

  const open = status === null && !chatOpen

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-body"
          initial={{ opacity: 0, y: reduce ? 0 : 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduce ? 0 : 16 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 md:px-6 md:pb-6 pointer-events-none"
        >
          <div className="mx-auto max-w-3xl pointer-events-auto bg-ivory border border-ivory-dark shadow-[0_20px_60px_-30px_rgba(58,42,29,0.45)]">
            <div className="p-5 md:p-6 flex flex-col md:flex-row gap-4 md:items-start">
              <div className="flex items-start gap-3 flex-1">
                <Cookie size={22} className="text-gold flex-shrink-0 mt-0.5" aria-hidden />
                <div className="flex-1">
                  <h2
                    id="cookie-consent-title"
                    className="font-display text-lg md:text-xl text-mocha"
                  >
                    {t('cookieConsent.title')}
                  </h2>
                  <p
                    id="cookie-consent-body"
                    className="font-body text-sm text-mocha/70 leading-relaxed mt-1"
                  >
                    {t('cookieConsent.body')}{' '}
                    <Link
                      to={`/${lang}/cookies`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold-ink hover:text-mocha transition-colors underline underline-offset-4"
                    >
                      {t('cookieConsent.policyLink')}
                    </Link>
                  </p>
                </div>
              </div>
              <div className="flex flex-row gap-2 md:flex-col md:items-stretch md:w-auto md:min-w-[10rem]">
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-1 md:flex-none justify-center"
                  onClick={() => {
                    consent.set('granted')
                    trackConsent('granted')
                  }}
                >
                  {t('cookieConsent.accept')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 md:flex-none justify-center"
                  onClick={() => {
                    consent.set('denied')
                    trackConsent('denied')
                  }}
                >
                  {t('cookieConsent.decline')}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
